import * as Sentry from '@sentry/nextjs';

import {
  OptionsParams,
  OptionInputParams,
  OptionPayoffParams,
  ImpliedVolatilityBisectParams,
  isPut,
  isLong,
  isShort,
  normcdf,
  normpdf,
  OPTIONS_PER_CONTRACT,
  YEAR_IN_MS,
  isCall,
  UnderlyingStockPosition,
  dateStringToMarketClose,
  INTRINSIC_LIMIT_TIME_YEARS,
  intrinsicOptionValue,
  expiryDelta,
} from './utils';
import {
  solveImpliedVolatility,
  ImpliedVolatilityResult,
  IVMethod,
  IVFailureReason,
} from 'utils/shared/quantlib/iv';
import { calculateReturnOnCost } from 'utils/shared/quantlib/returnOnCost';

const CAPTURE_ERRORS = false;

const captureException = (error: Error) => {
  if (CAPTURE_ERRORS) {
    Sentry.captureException(error);
  }
};

function toDollarsAndCents(value: number) {
  return parseFloat(value.toFixed(2));
}

export function calculateD1({
  spot,
  strike,
  timeToExpirationInYears,
  riskFreeRate,
  volatility,
}: Omit<OptionsParams, 'premium'>) {
  // Guard <= 0 (not just === 0): an expired option has negative time-to-expiry,
  // and √(negative) → NaN would silently poison every downstream Greek.
  const validExpiration = timeToExpirationInYears <= 0 ? 0.00000001 : timeToExpirationInYears;

  const d1 =
    (Math.log(spot / strike) + (riskFreeRate + (volatility * volatility) / 2) * validExpiration) /
    (volatility * Math.sqrt(validExpiration));

  return d1;
}

export function calculateD2({
  spot,
  strike,
  timeToExpirationInYears,
  riskFreeRate,
  volatility,
}: Omit<OptionsParams, 'premium'>) {
  // Guard <= 0 (not just === 0): an expired option has negative time-to-expiry,
  // and √(negative) → NaN would silently poison every downstream Greek.
  const validExpiration = timeToExpirationInYears <= 0 ? 0.00000001 : timeToExpirationInYears;

  const d2 =
    (Math.log(spot / strike) + (riskFreeRate - (volatility * volatility) / 2) * validExpiration) /
    (volatility * Math.sqrt(validExpiration));

  return d2;
}

export function calculateDelta(params: OptionsParams): number {
  const { spot, strike, type, timeToExpirationInYears } = params;

  // At expiry the greeks are singular — return the T→0 limit (see
  // INTRINSIC_LIMIT_TIME_YEARS). Delta becomes a step (±½ at the money).
  if (timeToExpirationInYears <= INTRINSIC_LIMIT_TIME_YEARS) {
    if (isCall(type)) return expiryDelta(spot, strike, true);
    if (isPut(type)) return expiryDelta(spot, strike, false);
    throw new Error('Option type must be either call or put');
  }

  const d1 = calculateD1(params);

  if (isCall(type)) {
    return normcdf(d1);
  } else if (isPut(type)) {
    return -normcdf(-d1);
  } else {
    throw new Error('Option type must be either call or put');
  }
}

export function calculateGamma(params: OptionsParams): number {
  const { spot, volatility, timeToExpirationInYears } = params;
  // Gamma → 0 in the T→0 limit (the finite-everywhere value; the singular spike
  // exactly at the strike is measure-zero and not meaningful to display).
  if (timeToExpirationInYears <= INTRINSIC_LIMIT_TIME_YEARS) return 0;
  // For a positive strike, the S→0 BSM limit has zero curvature. Evaluating
  // phi(-Infinity) / (S sigma sqrt(T)) at exactly S=0 would otherwise be 0/0.
  if (spot === 0 && params.strike > 0) return 0;
  const d1 = calculateD1(params);
  return normpdf(d1) / (spot * volatility * Math.sqrt(timeToExpirationInYears));
}

export function calculateRho(params: OptionsParams): number {
  const { strike, timeToExpirationInYears, riskFreeRate, type } = params;
  // Rho → 0 at expiry (no discounting horizon left).
  if (timeToExpirationInYears <= INTRINSIC_LIMIT_TIME_YEARS) return 0;
  const d2 = calculateD2(params);

  if (isCall(type)) {
    return (
      (strike *
        timeToExpirationInYears *
        Math.exp(-riskFreeRate * timeToExpirationInYears) *
        normcdf(d2)) /
      100
    );
  } else if (isPut(type)) {
    return (
      (-strike *
        timeToExpirationInYears *
        Math.exp(-riskFreeRate * timeToExpirationInYears) *
        normcdf(-d2)) /
      100
    );
  } else {
    throw new Error('Option type must be either call or put');
  }
}

export function calculateVega(params: OptionsParams): number {
  const { spot, timeToExpirationInYears } = params;
  // Vega → 0 at expiry (√T term vanishes).
  if (timeToExpirationInYears <= INTRINSIC_LIMIT_TIME_YEARS) return 0;
  const d1 = calculateD1(params);
  return (spot * Math.sqrt(timeToExpirationInYears) * normpdf(d1)) / 100;
}

/**
 * @note this is per day as per the standard
 */
const PER_DAY = 365;
export function calculateTheta(params: OptionsParams): number {
  const { spot, strike, timeToExpirationInYears, riskFreeRate, volatility, type } = params;
  // Theta → 0 at expiry (no time value left to decay; avoids the 1/√T blow-up).
  if (timeToExpirationInYears <= INTRINSIC_LIMIT_TIME_YEARS) return 0;
  const d1 = calculateD1(params);
  const d2 = calculateD2(params);

  if (isCall(type)) {
    return (
      (-(spot * normpdf(d1) * volatility) / (2 * Math.sqrt(timeToExpirationInYears)) -
        riskFreeRate * strike * Math.exp(-riskFreeRate * timeToExpirationInYears) * normcdf(d2)) /
      PER_DAY
    );
  } else if (isPut(type)) {
    return (
      (-(spot * normpdf(d1) * volatility) / (2 * Math.sqrt(timeToExpirationInYears)) +
        riskFreeRate * strike * Math.exp(-riskFreeRate * timeToExpirationInYears) * normcdf(-d2)) /
      PER_DAY
    );
  } else {
    throw new Error('Option type must be either call or put');
  }
}

export function calculatePremium(params: Omit<OptionsParams, 'premium'>): number {
  const { spot, strike, timeToExpirationInYears, riskFreeRate, type } = params;
  // At expiry an option is worth exactly its intrinsic value. Returning that
  // (rather than Black-Scholes at a sub-second stub) keeps the payoff a clean
  // piecewise-linear hockey stick with sharp corners at each strike.
  if (timeToExpirationInYears <= INTRINSIC_LIMIT_TIME_YEARS) {
    if (isCall(type)) return intrinsicOptionValue(spot, strike, true);
    if (isPut(type)) return intrinsicOptionValue(spot, strike, false);
    throw new Error('Option type must be either call or put');
  }
  const d1 = calculateD1(params);
  const d2 = calculateD2(params);

  if (isCall(type)) {
    return (
      spot * normcdf(d1) - strike * Math.exp(-riskFreeRate * timeToExpirationInYears) * normcdf(d2)
    );
  } else if (isPut(type)) {
    return (
      strike * Math.exp(-riskFreeRate * timeToExpirationInYears) * normcdf(-d2) -
      spot * normcdf(-d1)
    );
  } else {
    throw new Error('Option type must be either call or put');
  }
}

export function calculateBreakEvenBetweenBounds({
  lowerBound,
  upperBound,
  lowerBoundProfit,
  upperBoundProfit,
  optionLegs,
  underlyingStockPositions,
  dateAtCalculation,
  epsilon,
}: {
  lowerBound: number;
  upperBound: number;
  lowerBoundProfit: number;
  upperBoundProfit: number;
  optionLegs: Option[];
  underlyingStockPositions: UnderlyingStockPosition[];
  dateAtCalculation?: Date;
  epsilon: number;
}): number {
  // console.log({
  //   lowerBound,
  //   upperBound,
  //   lowerBoundProfit,
  //   upperBoundProfit,
  //   optionLegs,
  //   underlyingStockPositions,
  //   dateAtCalculation,
  //   epsilon,
  // });

  const isSignChange = lowerBoundProfit * upperBoundProfit < 0;

  if (!isSignChange) {
    throw new Error(
      'calculateBreakEvenBetweenBounds: Signs of lower and upper bounds must be different',
    );
  }

  let upperPrice = upperBound;
  let lowerPrice = lowerBound;
  let midPrice: number;
  let iterationBreakeven;

  const isNegativeSlope = lowerBoundProfit > upperBoundProfit;

  while (upperPrice - lowerPrice > epsilon) {
    midPrice = (upperPrice + lowerPrice) / 2;
    const checkPositionAtNewSpot = optionLegs.map(
      (leg) =>
        new Option({
          ...leg.params,
          spot: midPrice,
          dateAtCalculation,
          premium: null,
          timeToExpirationInYears: null,
        }),
    );
    const checkPnl = Option.calculateProfit(
      optionLegs,
      checkPositionAtNewSpot,
      underlyingStockPositions,
    );
    const midProfit = checkPnl.profit;

    if (Math.abs(midProfit) <= epsilon || Math.abs(upperPrice - lowerPrice) <= epsilon) {
      // Found a point close enough to zero profit, so consider it a breakeven point.
      iterationBreakeven = toDollarsAndCents(midPrice);
      break;
    }

    if (isNegativeSlope) {
      if (midProfit < 0) {
        upperPrice = midPrice;
      } else {
        lowerPrice = midPrice;
      }
    } else {
      if (midProfit > 0) {
        upperPrice = midPrice;
      } else {
        lowerPrice = midPrice;
      }
    }
  }

  if (iterationBreakeven) {
    return iterationBreakeven;
  } else {
    return toDollarsAndCents(upperPrice + lowerPrice) / 2;
  }
}

export function calculateBreakEvenSpotPrice(params: OptionsParams) {
  const { spot, strike, type, premium } = params;

  // Ensure that values are valid
  if (spot <= 0 || strike <= 0) {
    throw new Error('Spot and strike must be positive values.');
  }

  const effectivePremium = premium ?? calculatePremium(params);
  let breakEvenSpotPrice = 0;

  if (isCall(type)) {
    breakEvenSpotPrice = strike + effectivePremium;
  } else if (isPut(type)) {
    breakEvenSpotPrice = strike - effectivePremium;
  } else {
    throw new Error('Option type must be either call or put');
  }

  const breakEvenSpotPercentChange = ((breakEvenSpotPrice - spot) / spot) * 100;

  return { breakEvenSpotPrice, breakEvenSpotPercentChange };
}

const MAX_ITERATIONS_BISECT = 100;
const PRECISION_BISECT = 0.001;
const MIN_VOLATILITY = 0.001;
// Search ceiling for the IV solve. 500% covers 0DTE / meme / biotech names; the
// previous 100% ceiling excluded otherwise solvable high-volatility options.
const MAX_VOLATILITY = 5.0;
const INITIAL_IV_GUESS = 0.2;
// The production IV engine. Brent: same answer as bisection (verified to 3
// decimals on real chains) in ~3× fewer iterations, with better robustness on
// low-vega wing legs. Bisection/Newton remain available by name.
const DEFAULT_IV_METHOD: IVMethod = 'brent';

/**
 * Raw analytic vega, d(price)/d(sigma) = S·√T·φ(d1).
 *
 * NOTE: this deliberately does NOT reuse `calculateVega`, which divides by 100
 * to present vega "per 1% move" for display. Feeding that display-scaled value
 * to Newton-Raphson makes every step 100× too large and the solver diverges.
 */
function rawVega(params: ImpliedVolatilityBisectParams, volatility: number): number {
  const d1 = calculateD1({ ...params, volatility });
  return params.spot * Math.sqrt(params.timeToExpirationInYears) * normpdf(d1);
}

/**
 * Shared entry point into the pluggable IV layer. Builds the Black-Scholes
 * pricing + vega closures and runs the requested engine
 * (`bisection` | `newton` | `brent`) over a single, consistent bracket.
 */
function solveOptionIV(
  params: ImpliedVolatilityBisectParams,
  method: IVMethod,
): ImpliedVolatilityResult {
  const { premium, maxIterations = MAX_ITERATIONS_BISECT, precision = PRECISION_BISECT } = params;

  return solveImpliedVolatility({
    method,
    priceFn: (volatility) => calculatePremium({ ...params, volatility }),
    vegaFn: (volatility) => rawVega(params, volatility),
    marketPrice: premium,
    volLow: MIN_VOLATILITY,
    volHigh: MAX_VOLATILITY,
    priceTolerance: precision,
    maxIterations,
    initialGuess: INITIAL_IV_GUESS,
  });
}

export type OptionIVFailureReason =
  | IVFailureReason
  | 'invalid-input'
  | 'expired'
  | 'missing-premium';

/** JSON-safe caller boundary. A finite last iterate is NOT a successful solve. */
export type OptionIVResult = {
  method: IVMethod;
  iterations: number;
} & (
  | { status: 'solved'; converged: true; impliedVolatility: number; reason: null }
  | {
      status: 'unavailable';
      converged: false;
      impliedVolatility: null;
      reason: OptionIVFailureReason;
    }
);

export function unavailableOptionIV(
  reason: OptionIVFailureReason,
  method: IVMethod = DEFAULT_IV_METHOD,
): OptionIVResult {
  return {
    status: 'unavailable',
    converged: false,
    impliedVolatility: null,
    reason,
    method,
    iterations: 0,
  };
}

/** The production model, bracket and tolerances are unchanged. */
export function impliedVolatilityDetailed(
  params: ImpliedVolatilityBisectParams,
  method: IVMethod = DEFAULT_IV_METHOD,
): OptionIVResult {
  if (params.premium == null) return unavailableOptionIV('missing-premium', method);
  if (
    ![
      params.spot,
      params.strike,
      params.premium,
      params.riskFreeRate,
      params.timeToExpirationInYears,
    ].every(Number.isFinite) ||
    params.spot <= 0 ||
    params.strike <= 0 ||
    params.premium < 0 ||
    (!isCall(params.type) && !isPut(params.type))
  )
    return unavailableOptionIV('invalid-input', method);
  // The production pricer settles to intrinsic at this cutoff, so IV is not identifiable.
  if (params.timeToExpirationInYears <= INTRINSIC_LIMIT_TIME_YEARS)
    return unavailableOptionIV('expired', method);
  const result = solveOptionIV(params, method);

  if (
    result.converged &&
    Number.isFinite(result.impliedVolatility) &&
    result.impliedVolatility > 0
  ) {
    return { ...result, status: 'solved', converged: true, reason: null };
  }

  captureException(
    new Error(
      'Implied volatility not found in ' +
        (params.maxIterations ?? MAX_ITERATIONS_BISECT) +
        ' iterations (method=' +
        method +
        ', reason=' +
        result.reason +
        '). Value at error: ' +
        JSON.stringify({ premium: params.premium, params }, null, 2),
    ),
  );

  return {
    ...unavailableOptionIV(result.reason ?? 'no-convergence', method),
    iterations: result.iterations,
  };
}

/** Numeric convenience API; use the detailed API whenever failure is displayed or aggregated. */
export function impliedVolatility(
  params: ImpliedVolatilityBisectParams,
  method: IVMethod = DEFAULT_IV_METHOD,
): number | null {
  return impliedVolatilityDetailed(params, method).impliedVolatility;
}

export function impliedVolatilityBisect(params: ImpliedVolatilityBisectParams): number | null {
  return impliedVolatility(params, 'bisection');
}

export function impliedVolatilityBrent(params: ImpliedVolatilityBisectParams): number | null {
  return impliedVolatility(params, 'brent');
}

const MAX_ITERATIONS_VEGA = 100;
const PRECISION_VEGA = 0.001;
export function impliedVolatilityVega(params: ImpliedVolatilityBisectParams): number {
  const { maxIterations = MAX_ITERATIONS_VEGA, precision = PRECISION_VEGA } = params;

  const result = impliedVolatilityDetailed({ ...params, maxIterations, precision }, 'newton');

  // Preserve the historical throw-on-failure contract for this entry point.
  if (!result.converged) {
    throw new Error(
      'Implied volatility not found in ' +
        maxIterations +
        ' iterations (method=newton, reason=' +
        result.reason +
        ').',
    );
  }

  return result.impliedVolatility;
}

/**
 * @note old calucation, incorrect
 */
export function calculateProfitRange(params: OptionPayoffParams): {
  minProfit: number;
  maxProfit: number;
} {
  const { type, strike, spot, premium, numberOfContracts = 1 } = params;

  let minProfit: number;
  let maxProfit: number;

  if (isCall(type)) {
    if (isLong(numberOfContracts)) {
      minProfit = Math.max(spot - strike - premium, -premium);
      maxProfit = Infinity;
    } else if (isShort(numberOfContracts)) {
      minProfit = -Math.max(spot - strike, 0) + premium;
      maxProfit = premium;
    } else {
      throw new Error('Position must be either long or short');
    }
  } else if (isPut(type)) {
    const PRICE_GOES_TO_ZERO = 0;

    if (isLong(numberOfContracts)) {
      minProfit = Math.max(strike - spot - premium, -premium);
      maxProfit = strike - PRICE_GOES_TO_ZERO - premium;
    } else if (isShort(numberOfContracts)) {
      minProfit = -Math.max(strike - spot, 0) + premium;
      maxProfit = premium;
    } else {
      throw new Error('Position must be either long or short');
    }
  } else {
    throw new Error('Option type must be either call or put');
  }

  return { minProfit, maxProfit };
}

export function calculatePayoffAtSpot(params: OptionPayoffParams): number {
  const { type, strike, premium, numberOfContracts = 1, spot } = params;

  let payoff: number;

  if (numberOfContracts === 0) {
    return 0;
  }

  if (isCall(type)) {
    if (isLong(numberOfContracts)) {
      payoff = Math.max(spot - strike - premium, -premium);
    } else if (isShort(numberOfContracts)) {
      payoff = -Math.max(spot - strike, 0) + premium;
    } else {
      throw new Error('Position must be either long or short');
    }
  } else if (isPut(type)) {
    if (isLong(numberOfContracts)) {
      payoff = Math.max(strike - spot - premium, -premium);
    } else if (isShort(numberOfContracts)) {
      payoff = -Math.max(strike - spot, 0) + premium;
    } else {
      throw new Error('Position must be either long or short');
    }
  } else {
    throw new Error('Option type must be either call or put');
  }

  return payoff * numberOfContracts * OPTIONS_PER_CONTRACT;
}

/**
 * @note what it's doing is checking key points to see if they are profitable. If they are profitable, it adds the probability of that point to the aggregate probability.
 * It checks below the lowest break-even, above the highest break-even, and the mid-point between each break-even.
 * @todo I'm using payoff instead of profitability
 * Is there a way to use values that I've already calculated instead of redoing profits? It's purely checking > 0, so I don't need an actual profit value, just a sign change.
 * Maybe the breakevens also track a direction? Then I just can check if there was a positive or negative profit between each break-even.
 * UPDATE THE BREAKEVEN CODE THAT I JUST WROTE.
 */
export function calculateStrategyProbabilityOfProfit(
  optionLegs: Option[],
  underlyingStockPositions: UnderlyingStockPosition[],
  breakEvenPoints: number[],
  dateAtCalculation: Date,
): number {
  const breakEvens = breakEvenPoints;
  let aggregateProbability = 0;

  // 1. Evaluate below the lowest break-even
  const spotBelowLowestBreakEven = breakEvens[0] * 0.95;
  const positionAtChangeBelow = optionLegs.map((option) => {
    return new Option({
      ...option.params,
      spot: spotBelowLowestBreakEven,
      dateAtCalculation: dateAtCalculation,
      premium: null,
      timeToExpirationInYears: null,
    });
  });
  const belowLowestBreakEvenProfit = calculateProfit(
    optionLegs,
    positionAtChangeBelow,
    underlyingStockPositions,
  ).profit;
  if (belowLowestBreakEvenProfit > 0) {
    aggregateProbability += probabilityEndingBelowPrice(breakEvens[0], optionLegs[0].params);
  }

  // Loop through each segment between the break-evens
  for (let i = 1; i < breakEvens.length; i++) {
    const lowerBound = breakEvens[i - 1];
    const upperBound = breakEvens[i];
    const probLower = probabilityEndingBelowPrice(lowerBound, optionLegs[0].params);
    const probUpper = probabilityEndingBelowPrice(upperBound, optionLegs[0].params);

    // Check if the options position is profitable within the segment
    const midPrice = (upperBound + lowerBound) / 2;
    const positionAtChange = optionLegs.map((option) => {
      return new Option({
        ...option.params,
        spot: midPrice,
        dateAtCalculation: dateAtCalculation,
        premium: null,
        timeToExpirationInYears: null,
      });
    });
    const totalProfit = calculateProfit(
      optionLegs,
      positionAtChange,
      underlyingStockPositions,
    ).profit;

    if (totalProfit > 0) {
      aggregateProbability += probUpper - probLower;
    }
  }

  // 2. Evaluate above the highest break-even
  const spotAboveHighestBreakEven = breakEvens[breakEvens.length - 1] * 1.05;
  const positionAtChangeAbove = optionLegs.map((option) => {
    return new Option({
      ...option.params,
      spot: spotAboveHighestBreakEven,
      dateAtCalculation: dateAtCalculation,
      premium: null,
      timeToExpirationInYears: null,
    });
  });
  const aboveHighestBreakEvenProfit = calculateProfit(
    optionLegs,
    positionAtChangeAbove,
    underlyingStockPositions,
  ).profit;
  if (aboveHighestBreakEvenProfit > 0) {
    aggregateProbability +=
      1 - probabilityEndingBelowPrice(breakEvens[breakEvens.length - 1], optionLegs[0].params);
  }

  return aggregateProbability;
}

export function calculateTotalPayoffAtSpot(spot: number, optionLegs: Option[]): number {
  return optionLegs.reduce(
    (total, option) => total + calculatePayoffAtSpot({ ...option.params, spot }),
    0,
  );
}

function probabilityEndingBelowPrice(price: number, params: OptionsParams): number {
  // d2 seems to be an option but the numbers aren't intuitive: https://chat.openai.com/share/bb98e8c6-b783-46e0-8234-5ccf46d56872
  // const d2 = calculateD2({ ...params, spot: price - params.premium });
  // return normcdf(-d2);

  const { spot, volatility, timeToExpirationInYears } = params;
  const z = Math.log(price / spot) / (volatility * Math.sqrt(timeToExpirationInYears));

  return normcdf(z);
}

// POTENTIALLY A MONTE CARLO APPROACH TO CALCULATE PROBABILITY OF PROFIT
// const NUM_SIMULATIONS = 1000; // Adjust as needed

// function simulatePrice(s0: number, r: number, sigma: number, t: number): number {
//   const z = Math.random();
//   return s0 * Math.exp((r - 0.5 * sigma * sigma) * t + sigma * Math.sqrt(t) * z);
// }

// function calculateStrategyPayout(optionLeg: OptionInputParams, simulatedPrice: number): number {
//   switch (optionLeg.type) {
//     case 'CALL':
//       return Math.max(simulatedPrice - optionLeg.strike, 0) - optionLeg.premium;
//     case 'PUT':
//       return Math.max(optionLeg.strike - simulatedPrice, 0) - optionLeg.premium;
//   }
// }

// export function calculateProbabilityOfProfitMonteCarlo(optionLegs: OptionInputParams[]): number {
//   let profitableSimulations = 0;

//   for (let i = 0; i < NUM_SIMULATIONS; i++) {
//     let totalPayout = 0;
//     const simulatedPrice = simulatePrice(
//       optionLegs[0].spot,
//       optionLegs[0].riskFreeRate,
//       optionLegs[0].volatility,
//       optionLegs[0].timeToExpirationInYears
//     );

//     for (let leg of optionLegs) {
//       totalPayout += calculateStrategyPayout(leg, simulatedPrice);
//     }

//     if (totalPayout > 0) {
//       profitableSimulations++;
//     }
//   }

//   return profitableSimulations / NUM_SIMULATIONS;
// }

export function calculateNetDebit({
  premium,
  numberOfContracts,
}: {
  premium: number;
  numberOfContracts: number;
}): number {
  return premium * numberOfContracts * 100;
}

export function calculateNetDebitOrCredit(params: OptionsParams): number {
  const { premium, numberOfContracts = 1 } = params;

  return -1 * premium * numberOfContracts * OPTIONS_PER_CONTRACT;
}

export const calculateContractValue = ({
  premium = 0,
  numberOfContracts = 1,
}: {
  premium: number;
  numberOfContracts: number;
}): number => {
  return premium * numberOfContracts;
};

export function calculateAggregateContractValueOfOptionPosition(
  leg: Option[],
  shouldUseContractMultiplier: boolean,
) {
  let contractValue = 0;

  for (const option of leg) {
    const premium = option.params.premium ?? calculatePremium(option.params);

    contractValue += calculateContractValue({
      premium,
      numberOfContracts: option.params.numberOfContracts || 0,
    });
  }

  return contractValue * (shouldUseContractMultiplier ? OPTIONS_PER_CONTRACT : 1);
}

export function calculateUnderlyingStockPositionMetrics({
  underlyingStockPositions,
  currentPrice,
}: {
  underlyingStockPositions: UnderlyingStockPosition[];
  currentPrice: number;
}) {
  let totalProfit = 0;
  let totalCollateral = 0;
  let totalDelta = 0;
  let originalPositionValue = 0;
  let newPositionValue = 0;

  for (const underlyingStockPosition of underlyingStockPositions) {
    const profit =
      underlyingStockPosition.quantity * (currentPrice - underlyingStockPosition.price);
    totalProfit = totalProfit + profit;
    totalCollateral = totalCollateral + underlyingStockPosition.quantity * currentPrice;
    totalDelta = totalDelta + underlyingStockPosition.quantity;
    originalPositionValue =
      originalPositionValue + underlyingStockPosition.quantity * underlyingStockPosition.price;
    newPositionValue = newPositionValue + underlyingStockPosition.quantity * currentPrice;
  }

  return { totalProfit, totalCollateral, totalDelta, originalPositionValue, newPositionValue };
}

export function calculateProfit(
  originalPosition: Option[],
  positionAtChange: Option[],
  underlyingStockPositions: UnderlyingStockPosition[],
  spot?: number,
) {
  const currentStockPrice = spot ?? positionAtChange[0]?.params.spot ?? 0;
  const originalContractValue = calculateAggregateContractValueOfOptionPosition(
    originalPosition,
    false,
  );
  const newContractValue = calculateAggregateContractValueOfOptionPosition(positionAtChange, false);
  const profitFromOptions = newContractValue - originalContractValue;
  const {
    totalProfit: totalStockProfit,
    totalCollateral: _totalStockCollateral,
    originalPositionValue: originalStockPositionValue,
    newPositionValue: newStockPositionValue,
  } = calculateUnderlyingStockPositionMetrics({
    underlyingStockPositions,
    currentPrice: currentStockPrice,
  });
  const totalProfit = profitFromOptions * OPTIONS_PER_CONTRACT + totalStockProfit;
  // const maxRisk = isFinite(maxLoss) ? maxLoss : null;
  // const collateral = getPercentOfCollateral(positionAtChange) || 0 + totalStockCollateral;

  return {
    profitPerContract: profitFromOptions,
    profit: profitFromOptions * OPTIONS_PER_CONTRACT + totalStockProfit,
    ...calculateReturnOnCost({
      profit: totalProfit,
      netEntryCost: originalContractValue * OPTIONS_PER_CONTRACT + originalStockPositionValue,
    }),
    originalContractValue,
    newContractValue,
    totalStockProfit,
    originalStockPositionValue,
    newStockPositionValue,
    contractValue: newContractValue,
    // percentOfMaxRisk: maxRisk && (totalProfit / -maxRisk) * 100,
    // percentOfMaxCollateral: collateral && (totalProfit / -collateral) * 100,
  };
}

export function getPercentOfCollateral(options: Option[]) {
  if (options.length === 0) {
    return null;
  }

  let collateral = 0;
  options.forEach((option) => {
    const optionRequirement = getOptionRequirement({
      ...option.params,
    });
    const optionCollateral =
      (optionRequirement - option.params.premium) * 100 * (option.params.numberOfContracts || 1);
    collateral = collateral + (option.params.isLong ? -optionCollateral : -optionCollateral);
  });

  return collateral;
}

function getOptionRequirement({
  spot,
  strike,
  premium,
  isCall,
}: {
  spot: number;
  strike: number;
  premium: number;
  isCall: boolean;
}): number {
  const premiumComponent = premium + 0.1 * strike;

  if (isCall) {
    const marketComponent = Math.max(Math.abs(strike - spot), 0);
    return Math.max(premium + 0.2 * spot - marketComponent, premiumComponent);
  } else {
    const marketComponent = Math.max(Math.abs(spot - strike), 0);
    return Math.max(premium + 0.2 * spot - marketComponent, premiumComponent);
  }
}

type PayoffFunction = (spot: number) => number;

export function getPayoffFunction(option: Option): PayoffFunction {
  /**
   * @note Now that I'm tracking long through numberOfContracts, do we need as many if-else, or is it just handled?
   */
  let contracts = option.params.numberOfContracts;

  if (!contracts) {
    return () => 0;
  } else if (option.params.isCall && option.params.isLong) {
    return (spot: number) =>
      contracts *
      OPTIONS_PER_CONTRACT *
      (Math.max(0, spot - option.params.strike) - option.params.premium);
  } else if (option.params.isCall && option.params.isShort) {
    return (spot: number) =>
      contracts *
      OPTIONS_PER_CONTRACT *
      (-Math.max(0, spot - option.params.strike) + option.params.premium);
  } else if (option.params.isPut && option.params.isLong) {
    return (spot: number) =>
      contracts *
      OPTIONS_PER_CONTRACT *
      (Math.max(0, option.params.strike - spot) - option.params.premium);
  } else {
    return (spot: number) =>
      contracts *
      OPTIONS_PER_CONTRACT *
      (-Math.max(0, option.params.strike - spot) + option.params.premium);
  }
}

/**
 * @note we may need to cacluate derivative zeroes for each leg and then find the intersection of those
 * This potentially is a need for strategies with different expirations
 */
function calculateStrategyMaxProfitLoss({
  strategy,
  underlyingStockPositions,
  dateAtCalculation,
}: {
  strategy: Option[];
  underlyingStockPositions: UnderlyingStockPosition[];
  lowestSpot: number;
  highestSpot: number;
  stepUnit: number;
  dateAtCalculation: Date;
}): { maxProfit: number; maxLoss: number } {
  let maxProfit: number = -Infinity;
  let maxLoss: number = Infinity;
  let lastPayoff: number | null = null;

  const rightBound = getRightBound(strategy);

  let strikes = [...(strategy || []).map((option) => option.params.strike)];

  if (strikes.length === 0) {
    return { maxProfit: 0, maxLoss: 0 };
  }

  strikes = [0, ...strikes, rightBound, 2 * rightBound];

  strikes = strikes.sort((a, b) => a - b);

  for (let i = 0; i < strikes.length; i++) {
    const strike = strikes[i];
    const strategyAtSpots = strategy.map((option) => {
      return new Option({
        ...option.params,
        spot: strike,
        dateAtCalculation,
        premium: null,
        timeToExpirationInYears: null,
      });
    });
    const totalProfit = calculateProfit(strategy, strategyAtSpots, underlyingStockPositions).profit;

    maxProfit = Math.max(maxProfit, totalProfit);
    maxLoss = Math.min(maxLoss, totalProfit);

    if (lastPayoff !== null && i === strikes.length - 1 && lastPayoff !== totalProfit) {
      if (totalProfit > lastPayoff + 1e-4) {
        maxProfit = Infinity;
      } else if (totalProfit < lastPayoff - 1e-4) {
        maxLoss = -Infinity;
      }
    }

    lastPayoff = totalProfit;
  }

  // Adjust for commissions if necessary
  maxProfit = adjustForCommissions(maxProfit);
  maxLoss = adjustForCommissions(maxLoss);

  return { maxProfit, maxLoss };
}

function getRightBound(strategy: Option[]): number {
  return 100 * (strategy?.[0]?.params?.spot || 1);
}

export function getDerivativeZeroes(_strategy: Option[]): number[] {
  // Implement logic to find the zeroes of the derivative (using bisec)
  // Placeholder
  return []; // Default value for illustration
}

function adjustForCommissions(value: number): number {
  // Implement logic to adjust for commissions
  return value; // Default value for illustration
}

/**
 * Converts expiration input (string or Date) to a proper market close Date object
 * Handles Safari date parsing issues by splitting YYYY-MM-DD strings properly
 *
 * @param expirationInput - Either a Date object or "YYYY-MM-DD" string
 * @returns Date object set to market close (4 PM ET) on expiration date
 */
export function parseExpirationToMarketClose(
  expirationInput: Date | string | null | undefined,
): Date | null {
  if (!expirationInput) {
    return null;
  }

  // If it's already a Date object, assume it's correctly set to market close
  if (expirationInput instanceof Date) {
    return expirationInput;
  }

  // If it's a string, parse it safely to avoid Safari issues
  if (typeof expirationInput === 'string') {
    return dateStringToMarketClose(expirationInput);
  }

  return null;
}

/**
 * Calculates time to expiration in years, accepting both string and Date inputs
 * Automatically converts string dates to market close time (4 PM ET)
 *
 * @param expiration - Date object, "YYYY-MM-DD" string, or null/undefined
 * @param dateAtCalculation - Optional calculation date (defaults to current time)
 * @returns Time to expiration in years, or very small positive number if expired
 */
export const calculateTimeToExpirationInYears = (
  expiration: Date | string | null | undefined,
  dateAtCalculation?: Date,
): number => {
  const calculationDate = dateAtCalculation || new Date();

  // Convert expiration to proper market close Date
  const expirationDate = parseExpirationToMarketClose(expiration);

  if (!expirationDate) {
    throw new Error('Invalid expiration date provided');
  }

  const expirationTime = expirationDate.getTime();
  const calculationTime = calculationDate.getTime();

  const timeToExpiration = (expirationTime - calculationTime) / YEAR_IN_MS;

  // If the option has expired, return a very small positive number
  // This prevents division by zero in pricing models while indicating expiration
  if (timeToExpiration <= 0) {
    return 0.0000000001; // Very small positive number as requested
  }

  return timeToExpiration;
};

export class Option {
  params: OptionsParams;

  static calculateProfit = calculateProfit;
  static calculateStrategyProbabilityOfProfit = calculateStrategyProbabilityOfProfit;
  static calculateStrategyMaxProfitLoss = calculateStrategyMaxProfitLoss;
  static calculateUnderlyingStockPositionMetrics = calculateUnderlyingStockPositionMetrics;
  static calculateBreakEvenBetweenBounds = calculateBreakEvenBetweenBounds;
  // static calculateProbabilityOfProfitMonteCarlo = calculateProbabilityOfProfitMonteCarlo;

  constructor(params: OptionInputParams) {
    if (params.timeToExpirationInYears == null && !params.expirationDateString) {
      throw new Error('Must provide an expiration');
    }

    const timeToExpirationInYears =
      params.timeToExpirationInYears ??
      calculateTimeToExpirationInYears(
        params.expirationDateString,
        params.dateAtCalculation || new Date(),
      );

    const calculatedPremium: number | undefined = calculatePremium({
      ...params,
      timeToExpirationInYears,
      isCall: isCall(params.type),
      isPut: isPut(params.type),
      isLong: !!params.numberOfContracts && isLong(params.numberOfContracts),
      isShort: !!params.numberOfContracts && isShort(params.numberOfContracts),
    });
    const premium = params.premium ?? calculatedPremium;

    this.params = {
      ...params,
      premium,
      calculatedPremium,
      isUsingCalculatedPremium: params.premium == null,
      timeToExpirationInYears,
      expirationDateString: params.expirationDateString,
      isCall: isCall(params.type),
      isPut: isPut(params.type),
      isLong: !!params.numberOfContracts && isLong(params.numberOfContracts || 0),
      isShort: !!params.numberOfContracts && isShort(params.numberOfContracts || 0),
    };
  }

  public calculateImpliedVolatilityBisect(premium: number): number | null {
    return impliedVolatilityBisect({ ...this.params, premium });
  }

  public calculateImpliedVolatilityVega(premium: number): number {
    return impliedVolatilityVega({ ...this.params, premium });
  }

  /**
   * Pluggable IV solve. Defaults to the production engine (Brent); pass
   * `'bisection'` or `'newton'` to A/B a different root-finder against it.
   */
  public calculateImpliedVolatility(
    premium: number,
    method: IVMethod = DEFAULT_IV_METHOD,
  ): number | null {
    return impliedVolatility({ ...this.params, premium }, method);
  }

  public calculateImpliedVolatilityDetailed(method: IVMethod = DEFAULT_IV_METHOD): OptionIVResult {
    if (this.params.isUsingCalculatedPremium) return unavailableOptionIV('missing-premium', method);
    return impliedVolatilityDetailed(this.params, method);
  }

  public calculatePremium(): number {
    return calculatePremium(this.params);
  }

  public calculateGreeks() {
    const delta = calculateDelta(this.params);
    const gamma = calculateGamma(this.params);
    const rho = calculateRho(this.params);
    const vega = calculateVega(this.params);
    const theta = calculateTheta(this.params);

    return {
      delta: delta,
      gamma: gamma,
      rho: rho,
      vega: vega,
      theta: theta,
    };
  }

  /**
   * @todo use a flag or make premium optional and calculate if it doesn't exist?
   */
  public calculateProperties() {
    // const d1 = calculateD1(this.params);
    // const d2 = calculateD2(this.params);

    const delta = calculateDelta(this.params);
    const gamma = calculateGamma(this.params);
    const rho = calculateRho(this.params);
    const vega = calculateVega(this.params);
    const theta = calculateTheta(this.params);
    const netDebit = calculateNetDebitOrCredit(this.params);
    const payoffAtSpot = calculatePayoffAtSpot(this.params);
    const value =
      calculateContractValue({
        numberOfContracts: this.params.numberOfContracts || 0,
        premium: this.params.premium,
      }) * OPTIONS_PER_CONTRACT;
    const impliedVolatilityResult = this.calculateImpliedVolatilityDetailed();

    // NOTE: Add back if needed, but trying to minimize calculations
    // const { breakEvenSpotPrice, breakEvenSpotPercentChange } = calculateBreakEvenSpotPrice(
    //   this.params,
    // );
    // const profitRange = calculateProfitRange(this.params);

    // TODO: Implied volatility

    return {
      // d1,
      // d2,
      // breakEvenSpotPrice,
      // breakEvenSpotPercentChange,
      // profitRange,
      delta,
      gamma,
      rho,
      vega,
      theta,
      premium: this.params.premium,
      calculatedPremium: this.params.calculatedPremium,
      isUsingCalculatedPremium: this.params.isUsingCalculatedPremium,
      payoffAtSpot,
      netDebit,
      value,
      impliedVolatility: impliedVolatilityResult.impliedVolatility,
      impliedVolatilityResult,
    };
  }
}

// ---+++--- Transaction cost version, need to test ---+++---
// ---+++--- Transaction cost version, need to test ---+++---
// ---+++--- Transaction cost version, need to test ---+++---

export interface TransactionCostParams {
  commission: number; // flat fee per trade
  perContractFee: number; // fee per contract
}

export interface OptionsParamsWithCost extends OptionsParams, TransactionCostParams {
  numberOfContracts?: number;
}

export interface OptionPayoffParamsWithCost extends OptionPayoffParams, TransactionCostParams {
  numberOfContracts?: number;
}

export function calculatePremiumWithCost(params: OptionsParamsWithCost): number {
  const basePrice = calculatePremium(params);
  const { commission, perContractFee, numberOfContracts = 1 } = params;
  const totalTransactionCost = commission + perContractFee * numberOfContracts;

  return basePrice - totalTransactionCost;
}

/**
 * @note old calucation, incorrect
 */
export function calculatePayoffRangeWithCost(params: OptionPayoffParamsWithCost): {
  minProfit: number;
  maxProfit: number;
} {
  const baseProfit = calculateProfitRange(params);
  const { commission, perContractFee, numberOfContracts = 1 } = params;
  const totalTransactionCost = commission + perContractFee * numberOfContracts;

  return {
    minProfit: baseProfit.minProfit - totalTransactionCost,
    maxProfit: baseProfit.maxProfit - totalTransactionCost,
  };
}

export class OptionWithCost extends Option {
  // @ts-ignore
  private costParams: TransactionCostParams;

  constructor(
    params: OptionsParams &
      OptionPayoffParams &
      ImpliedVolatilityBisectParams &
      TransactionCostParams & { numberOfContracts?: number },
  ) {
    super(params);
    this.costParams = {
      commission: params.commission,
      perContractFee: params.perContractFee,
    };
  }

  // public calculatePremium(): number {
  //   return this.calculateProperties().priceWithCost;
  // }

  // public calculateProperties() {
  //   const baseProperties = super.calculateProperties();
  //   const { commission, perContractFee } = this.costParams;
  //   const numberOfContracts = this.params.numberOfContracts || 1;
  //   const totalTransactionCost = commission + perContractFee * numberOfContracts;

  //   const priceWithCost = baseProperties.premium - totalTransactionCost;
  //   const payoffWithCost = {
  //     minProfit: baseProperties.profitRange.minProfit - totalTransactionCost,
  //     maxProfit: baseProperties.profitRange.maxProfit - totalTransactionCost,
  //   };

  //   return {
  //     ...baseProperties,
  //     priceWithCost,
  //     payoffWithCost,
  //   };
  // }
}

export function calculateBreakEvenSpotPriceWithCost(_params: OptionsParamsWithCost) {
  /**
   * @todo complete
   */
  return { breakEvenSpotPriceWithCost: 0, breakEvenSpotPercentChangeWithCost: 0 };
}

/**
 * Converts a Date object back to YYYY-MM-DD string format
 * Useful for maintaining consistency when recreating Option objects
 *
 * @param date - Date object to convert
 * @returns YYYY-MM-DD string
 */
export function dateToDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Gets the original expiration date string from an Option's params
 * If the option was created with a Date, converts it back to string format
 *
 * @param option - Option object to extract expiration from
 * @returns YYYY-MM-DD string representing the expiration date
 */
export function getExpirationDateString(option: Option): string {
  // If we stored the original string, use that
  if (option.params.expirationDateString) {
    return option.params.expirationDateString;
  }

  throw new Error(
    'Option has no expiration date string - use expirationDateString when creating options',
  );
}
